import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError } from '@/api/client'
import { adminApi, billingApi } from '@/api/endpoints'
import { Button } from '@/components/ui/Button'
import { FieldShell, TextArea, TextField } from '@/components/ui/Field'
import { Modal } from '@/components/ui/Modal'
import { StatusPill, type Tone } from '@/components/ui/StatusPill'
import { cn, formatDate, humanise, relativeTime } from '@/lib/utils'
import { toast } from '@/store/toast'
import type {
  BillingPeriod,
  LicenseErrorCode,
  PlanKey,
  PlansResponse,
  SubscriptionResponse,
  SubscriptionStatus,
  UserDetail,
} from '@/types/api'

const HISTORY_PREVIEW = 4

/** Used when `/billing/plans` cannot be read, so a grant still works. */
const FALLBACK_PLANS: Array<{ key: PlanKey; name: string; monthlyApplications: number; perMonthCents: Partial<Record<BillingPeriod, number>> }> = [
  { key: 'starter', name: 'Starter', monthlyApplications: 300, perMonthCents: {} },
  { key: 'pro', name: 'Pro', monthlyApplications: 1000, perMonthCents: {} },
  { key: 'power', name: 'Power', monthlyApplications: 2000, perMonthCents: {} },
]
const FALLBACK_PERIODS: PlansResponse['periods'] = [
  { key: 'monthly', months: 1, label: 'Monthly' },
  { key: 'quarterly', months: 3, label: 'Quarterly' },
  { key: 'annual', months: 12, label: 'Annual' },
]

const STATUS_TONE: Record<SubscriptionStatus, Tone> = {
  active: 'success',
  past_due: 'warning',
  incomplete: 'warning',
  canceled: 'neutral',
  expired: 'neutral',
}

const REASON_TEXT: Record<LicenseErrorCode, string> = {
  subscription_required: 'No plan: they cannot start applications.',
  subscription_inactive: 'The plan is not in force: they cannot start applications.',
  quota_exhausted: "This month's applications are used up.",
  feature_not_in_plan: 'Something they use is not in this plan.',
}

/** "Applications left" for the side column: the plan's remaining allowance, in place of the old credits. */
export function applicationsLeft(subscription: SubscriptionResponse | undefined): React.ReactNode {
  if (!subscription) return '–'
  if (!subscription.usage) return <span className="text-warning">No plan</span>
  const { remaining, limit } = subscription.usage
  return (
    <span className={cn('num', remaining === 0 && 'text-danger')}>
      {remaining.toLocaleString()} <span className="text-ink-faint">/ {limit.toLocaleString()}</span>
    </span>
  )
}

/**
 * The user's plan: what it is, how much of this month's allowance is used,
 * what happened to it, and what an administrator can do — give a plan without
 * payment, change a running grant's plan or end date, or end it. A paid (Stripe) subscription is
 * the user's own; the server refuses to replace it.
 */
export function SubscriptionPanel({ user }: { user: UserDetail }) {
  const queryClient = useQueryClient()
  const [granting, setGranting] = useState(false)
  const [ending, setEnding] = useState(false)
  const [allHistory, setAllHistory] = useState(false)
  const data = user.subscription
  const name = user.fullName || user.email

  const onSaved = (next: SubscriptionResponse) => {
    queryClient.setQueryData<UserDetail>(['user', user.id], (old) => (old ? { ...old, subscription: next } : old))
    void queryClient.invalidateQueries({ queryKey: ['user', user.id] })
    void queryClient.invalidateQueries({ queryKey: ['users'] })
  }

  const end = useMutation({
    mutationFn: () => adminApi.endSubscriptionGrant(user.id),
    onSuccess: (next) => {
      setEnding(false)
      onSaved(next)
      toast.success('Grant ended', `${name} no longer has the granted plan.`)
    },
    onError: (error) => {
      setEnding(false)
      toast.error('Could not end the grant', error instanceof ApiError ? error.message : undefined)
    },
  })

  if (!data) {
    return (
      <section className="panel divide-y divide-line">
        <p className="label-eyebrow px-4 py-3">Subscription</p>
        <p className="px-4 py-3 text-[13px] text-ink-muted">This server does not report plans yet.</p>
      </section>
    )
  }

  const sub = data.subscription
  const usage = data.usage
  const history = allHistory ? data.history : data.history.slice(0, HISTORY_PREVIEW)
  const canEnd = sub?.source === 'admin' && data.active
  const ends = sub && (sub.cancelAtPeriodEnd || !sub.renewsAt)

  return (
    <section className="panel divide-y divide-line">
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <p className="label-eyebrow">Subscription</p>
        {sub ? (
          <StatusPill size="sm" tone={STATUS_TONE[sub.status] ?? 'neutral'}>
            {sub.cancelAtPeriodEnd && sub.status === 'active' ? 'Ending' : humanise(sub.status)}
          </StatusPill>
        ) : (
          <StatusPill size="sm">No plan</StatusPill>
        )}
      </div>

      {sub ? (
        <>
          <Fact label="Plan">
            <span className="font-medium text-ink">{sub.planName}</span> · {humanise(sub.period)}
          </Fact>
          <Fact label="Source">{sub.source === 'admin' ? <span className="text-brand">Granted by an admin</span> : 'Paid (Stripe)'}</Fact>
          <Fact label="Term">
            <span className="num text-[12.5px]">
              {formatDate(sub.currentPeriodStart)} – {formatDate(sub.currentPeriodEnd)}
            </span>
          </Fact>
          <Fact label={ends ? 'Ends' : 'Renews'}>
            <span className="num text-[12.5px]">{formatDate(ends ? sub.currentPeriodEnd : sub.renewsAt)}</span>
          </Fact>
        </>
      ) : (
        <p className="px-4 py-3 text-[13px] text-ink-muted">No plan. They cannot start applications until they subscribe or are granted one.</p>
      )}

      {usage && <Usage usage={usage} />}

      {!data.canApply && data.reason && sub && <p className="px-4 py-2.5 text-[12.5px] leading-relaxed text-warning">{REASON_TEXT[data.reason] ?? humanise(data.reason)}</p>}

      <div className="flex flex-wrap gap-2 px-4 py-3">
        <Button size="xs" variant="secondary" onClick={() => setGranting(true)}>
          {canEnd ? 'Change plan or end date' : 'Grant plan'}
        </Button>
        {canEnd && (
          <Button size="xs" variant="ghost" className="text-danger hover:text-danger" onClick={() => setEnding(true)}>
            End grant
          </Button>
        )}
      </div>

      {data.history.length > 0 && (
        <div className="px-4 py-3">
          <p className="mb-2 text-[12.5px] text-ink-muted">History</p>
          <ol className="space-y-2">
            {history.map((event, index) => (
              <li key={`${event.at}-${index}`} className="flex gap-2.5 text-[12.5px]">
                <span className={cn('mt-[6px] h-1.5 w-1.5 shrink-0 rounded-full', event.type.startsWith('admin') || event.type.includes('grant') ? 'bg-brand' : 'bg-ink-faint')} />
                <div className="min-w-0 flex-1">
                  <p className="leading-relaxed text-ink-soft">{event.message || humanise(event.type)}</p>
                  <p className="num text-[11.5px] text-ink-faint" title={new Date(event.at).toLocaleString()}>
                    {relativeTime(event.at)}
                  </p>
                </div>
              </li>
            ))}
          </ol>
          {data.history.length > HISTORY_PREVIEW && (
            <button type="button" onClick={() => setAllHistory(!allHistory)} className="mt-2 text-[12.5px] font-medium text-ink-muted hover:text-ink">
              {allHistory ? 'Show fewer' : `Show all ${data.history.length}`}
            </button>
          )}
        </div>
      )}

      <PlanModal open={granting} onClose={() => setGranting(false)} user={user} current={data} onSaved={onSaved} />

      <Modal
        open={ending}
        onClose={() => !end.isPending && setEnding(false)}
        size="sm"
        title="End the grant now?"
        description={`For ${name}.`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEnding(false)} disabled={end.isPending}>
              Cancel
            </Button>
            <Button variant="danger" loading={end.isPending} onClick={() => end.mutate()}>
              End grant
            </Button>
          </>
        }
      >
        <p className="text-[13.5px] leading-relaxed text-ink-soft">
          {sub ? (
            <>
              <span className="font-medium text-ink">{sub.planName}</span> stops now instead of on {formatDate(sub.currentPeriodEnd)}.
            </>
          ) : (
            'The granted plan stops now.'
          )}{' '}
          They cannot start new applications until they subscribe or are granted a plan again.
        </p>
      </Modal>
    </section>
  )
}

function Usage({ usage }: { usage: NonNullable<SubscriptionResponse['usage']> }) {
  const pct = (value: number) => (usage.limit > 0 ? Math.min(100, (value / usage.limit) * 100) : 0)
  const usedPct = pct(usage.used)
  const runningPct = Math.min(100 - usedPct, pct(usage.inProgress))
  return (
    <div className="px-4 py-3">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-[12.5px] text-ink-muted">This month</span>
        <span className="num text-[13.5px] text-ink">
          {usage.used.toLocaleString()} <span className="text-ink-faint">/ {usage.limit.toLocaleString()}</span>
        </span>
      </div>
      <div
        className="mt-2 flex h-1.5 overflow-hidden rounded-pill bg-ink/[0.07]"
        role="meter"
        aria-label="Applications used this month"
        aria-valuemin={0}
        aria-valuemax={usage.limit}
        aria-valuenow={usage.used + usage.inProgress}
      >
        <span className="h-full bg-success" style={{ width: `${usedPct}%` }} />
        <span className="h-full bg-info" style={{ width: `${runningPct}%` }} />
      </div>
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[12px] text-ink-muted">
        <span>
          <span className="num font-semibold text-success">{usage.used.toLocaleString()}</span> sent
        </span>
        <span>
          <span className="num font-semibold text-info">{usage.inProgress.toLocaleString()}</span> in progress
        </span>
        <span>
          <span className={cn('num font-semibold', usage.remaining === 0 ? 'text-danger' : 'text-ink')}>{usage.remaining.toLocaleString()}</span> left
        </span>
      </div>
      <p className="mt-1 text-[12px] text-ink-faint">Resets {formatDate(usage.windowEnd)}</p>
    </div>
  )
}

// ---- Granting or changing a plan ----------------------------------------------------

/** yyyy-mm-dd in local time, for a date input. */
function dateInput(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}
function plusMonths(from: Date, months: number): Date {
  const out = new Date(from)
  const day = out.getDate()
  out.setDate(1)
  out.setMonth(out.getMonth() + months)
  out.setDate(Math.min(day, new Date(out.getFullYear(), out.getMonth() + 1, 0).getDate()))
  return out
}
/** The grant runs to the end of the chosen day, local time. */
function endOfDay(value: string): Date {
  return new Date(`${value}T23:59:59`)
}

const EXTEND: Array<{ label: string; months: number }> = [
  { label: '+1 month', months: 1 },
  { label: '+3 months', months: 3 },
  { label: '+6 months', months: 6 },
  { label: '+1 year', months: 12 },
]

type Who = { id: string; fullName: string | null; email: string }

/**
 * Give a plan without payment, or change the running grant: its plan and the
 * day it ends. Changing keeps the grant's start, so this month's usage stays;
 * a new grant starts today and replaces the old one.
 */
export function PlanModal({
  open,
  onClose,
  user,
  current,
  onSaved,
}: {
  open: boolean
  onClose: () => void
  user: Who
  current: SubscriptionResponse
  onSaved: (next: SubscriptionResponse) => void
}) {
  const plansQuery = useQuery({ queryKey: ['billing-plans'], queryFn: billingApi.plans, staleTime: 10 * 60_000, enabled: open })
  const running = current.subscription?.source === 'admin' && current.active ? current.subscription : null
  const paid = current.subscription?.source === 'stripe' && current.active
  const [mode, setMode] = useState<'change' | 'grant'>(running ? 'change' : 'grant')
  const [plan, setPlan] = useState<PlanKey>(current.subscription?.plan ?? 'pro')
  const [period, setPeriod] = useState<BillingPeriod>(current.subscription?.period ?? 'monthly')
  const [ends, setEnds] = useState(() => dateInput(running ? new Date(running.currentPeriodEnd) : plusMonths(new Date(), 1)))
  const [note, setNote] = useState('')

  // Each time it opens, start from what the user has now.
  useEffect(() => {
    if (!open) return
    setMode(running ? 'change' : 'grant')
    setPlan(current.subscription?.plan ?? 'pro')
    setPeriod(current.subscription?.period ?? 'monthly')
    setEnds(dateInput(running ? new Date(running.currentPeriodEnd) : plusMonths(new Date(), FALLBACK_PERIODS.find((p) => p.key === (current.subscription?.period ?? 'monthly'))?.months ?? 1)))
    setNote('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const plans = plansQuery.data?.plans.length
    ? plansQuery.data.plans.map((p) => ({
        key: p.key,
        name: p.name,
        monthlyApplications: p.monthlyApplications,
        perMonthCents: Object.fromEntries(Object.entries(p.prices).map(([k, price]) => [k, price.perMonthCents])) as Partial<Record<BillingPeriod, number>>,
      }))
    : FALLBACK_PLANS
  const periods = plansQuery.data?.periods.length ? plansQuery.data.periods : FALLBACK_PERIODS
  const currency = (plansQuery.data?.currency ?? 'usd').toUpperCase()
  const chosen = plans.find((p) => p.key === plan)

  const today = dateInput(new Date())
  const latest = dateInput(plusMonths(new Date(), 120))
  const endsError = !ends ? 'Pick the day it ends.' : ends < today ? 'Pick today or a later day. To stop it now, use End grant.' : ends > latest ? 'At most 10 years ahead.' : null
  const changing = mode === 'change' && running !== null
  const planChanged = changing && plan !== running.plan
  const endChanged = changing && ends !== dateInput(new Date(running.currentPeriodEnd))
  const nothingToSave = changing && !planChanged && !endChanged

  const pickPeriod = (key: BillingPeriod) => {
    setPeriod(key)
    setEnds(dateInput(plusMonths(new Date(), periods.find((p) => p.key === key)?.months ?? 1)))
  }
  const extend = (months: number) => {
    const base = running ? new Date(running.currentPeriodEnd) : new Date()
    setEnds(dateInput(plusMonths(base > new Date() ? base : new Date(), months)))
  }

  const save = useMutation({
    mutationFn: () => {
      const endsAt = endOfDay(ends).toISOString()
      const memo = note.trim() ? { note: note.trim() } : {}
      return changing
        ? adminApi.changeSubscriptionGrant(user.id, { ...(planChanged && { plan }), ...(endChanged && { endsAt }), ...memo })
        : adminApi.grantSubscription(user.id, { plan, period, endsAt, ...memo })
    },
    onSuccess: (next) => {
      onSaved(next)
      onClose()
      const sub = next.subscription
      toast.success(sub ? `${sub.planName} ${changing ? 'updated' : 'granted'}` : 'Plan saved', sub ? `Until ${formatDate(sub.currentPeriodEnd)}.` : undefined)
    },
    onError: (error) => {
      if (error instanceof ApiError && error.status === 409) {
        toast.error('Not saved', error.message)
        return
      }
      toast.error(changing ? 'Could not change the plan' : 'Could not grant the plan', error instanceof ApiError ? error.message : undefined)
    },
  })

  const money = (cents: number) => new Intl.NumberFormat('en', { style: 'currency', currency, maximumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100)

  return (
    <Modal
      open={open}
      onClose={() => !save.isPending && onClose()}
      size="sm"
      title={changing ? 'Change plan or end date' : 'Grant a plan'}
      description={`For ${user.fullName || user.email}. No payment is taken.`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button loading={save.isPending} disabled={!!endsError || nothingToSave || paid} onClick={() => save.mutate()}>
            {changing ? 'Save changes' : `Grant ${chosen?.name ?? 'plan'}`}
          </Button>
        </>
      }
    >
      <div className="space-y-5 pb-2">
        {paid && (
          <p className="rounded-field bg-warning/[0.1] px-3 py-2 text-[13px] leading-relaxed text-warning">
            {user.fullName || 'This user'} pays for {current.subscription?.planName} through Stripe. Change a paid plan in Stripe; a grant cannot replace it.
          </p>
        )}

        {running && (
          <div role="radiogroup" aria-label="What to do" className="flex gap-1.5">
            {(
              [
                ['change', 'Change this grant'],
                ['grant', 'Start a new grant'],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="radio"
                aria-checked={mode === key}
                onClick={() => {
                  setMode(key)
                  setEnds(dateInput(key === 'change' ? new Date(running.currentPeriodEnd) : plusMonths(new Date(), periods.find((p) => p.key === period)?.months ?? 1)))
                  if (key === 'change') setPlan(running.plan)
                }}
                className={cn(
                  'h-8 flex-1 rounded-pill border px-3 text-[12.5px] font-medium transition-colors',
                  mode === key ? 'border-ink bg-ink text-oncolor' : 'border-line bg-surface text-ink-soft hover:bg-surface-muted',
                )}
              >
                {label}
              </button>
            ))}
          </div>
        )}
        {running && (
          <p className="rounded-field bg-ink/[0.05] px-3 py-2 text-[13px] leading-relaxed text-ink-soft">
            {changing ? (
              <>
                Now: <span className="font-medium text-ink">{running.planName}</span> until {formatDate(running.currentPeriodEnd)}. It keeps its start, so this month's usage stays.
              </>
            ) : (
              <>
                Replaces the current grant ({running.planName}, ends {formatDate(running.currentPeriodEnd)}). The new one starts today with a fresh monthly allowance.
              </>
            )}
          </p>
        )}

        <FieldShell label="Plan">
          <div role="radiogroup" aria-label="Plan" className="grid gap-2">
            {plans.map((option) => {
              const cents = option.perMonthCents[period]
              return (
                <button
                  key={option.key}
                  type="button"
                  role="radio"
                  aria-checked={plan === option.key}
                  onClick={() => setPlan(option.key)}
                  className={cn(
                    'flex items-center justify-between gap-3 rounded-field border px-3.5 py-2.5 text-left transition-colors',
                    plan === option.key ? 'border-ink bg-surface-muted' : 'border-line bg-surface hover:bg-surface-muted',
                  )}
                >
                  <span className="min-w-0">
                    <span className="block text-[14px] font-medium text-ink">{option.name}</span>
                    <span className="block text-[12.5px] text-ink-muted">
                      <span className="num">{option.monthlyApplications.toLocaleString()}</span> applications a month
                    </span>
                  </span>
                  {cents !== undefined && <span className="num shrink-0 text-[12.5px] text-ink-muted">{money(cents)}/mo</span>}
                </button>
              )
            })}
          </div>
        </FieldShell>

        {!changing && (
          <FieldShell label="Period" hint="Sets the end date below; change the date if you need another one.">
            <div role="radiogroup" aria-label="Period" className="flex gap-1.5">
              {periods.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  role="radio"
                  aria-checked={period === option.key}
                  onClick={() => pickPeriod(option.key)}
                  className={cn(
                    'h-8 flex-1 rounded-pill border px-3 text-[12.5px] font-medium transition-colors',
                    period === option.key ? 'border-ink bg-ink text-oncolor' : 'border-line bg-surface text-ink-soft hover:bg-surface-muted',
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </FieldShell>
        )}

        <div>
          <TextField
            label="Ends on"
            appearance="box"
            type="date"
            min={today}
            max={latest}
            value={ends}
            onChange={(event) => setEnds(event.target.value)}
            error={endsError}
            hint={ends && !endsError ? `The plan works until the end of ${formatDate(endOfDay(ends).toISOString())}.` : undefined}
            className="text-[14px]"
          />
          {changing && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {EXTEND.map((option) => (
                <button
                  key={option.months}
                  type="button"
                  onClick={() => extend(option.months)}
                  className="h-7 rounded-pill border border-line bg-surface px-2.5 text-[12px] font-medium text-ink-soft transition-colors hover:bg-surface-muted"
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <TextArea
          label="Note"
          appearance="box"
          rows={2}
          maxLength={500}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="Why, for the record (optional)"
          className="text-[14px]"
        />
      </div>
    </Modal>
  )
}

/**
 * The same, for a row of the users list: reads that user's subscription when
 * opened, then shows the plan dialog.
 */
export function ManagePlanModal({ open, onClose, user }: { open: boolean; onClose: () => void; user: Who }) {
  const queryClient = useQueryClient()
  const detail = useQuery({ queryKey: ['user', user.id], queryFn: () => adminApi.user(user.id), enabled: open })
  const current = detail.data?.subscription
  const onSaved = (next: SubscriptionResponse) => {
    queryClient.setQueryData<UserDetail>(['user', user.id], (old) => (old ? { ...old, subscription: next } : old))
    void queryClient.invalidateQueries({ queryKey: ['user', user.id] })
    void queryClient.invalidateQueries({ queryKey: ['users'] })
  }
  if (open && !current) {
    return (
      <Modal open={open} onClose={onClose} size="sm" title="Plan" description={`For ${user.fullName || user.email}.`}>
        <p className="pb-4 text-[13.5px] text-ink-muted">{detail.isError ? 'Could not read this user. Try again.' : 'Reading their plan…'}</p>
      </Modal>
    )
  }
  return current ? <PlanModal open={open} onClose={onClose} user={user} current={current} onSaved={onSaved} /> : null
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 px-4 py-2.5">
      <span className="shrink-0 text-[12.5px] text-ink-muted">{label}</span>
      <span className="min-w-0 text-right text-[13.5px] text-ink-soft">{children}</span>
    </div>
  )
}
